/**
 * Drug Interaction Service
 * 
 * Checks for potential drug interactions between medications using AI.
 * Warns users before they add a medication that may interact with
 * their existing medications.
 * 
 * ⚠️ DISCLAIMER: This is for INFORMATIONAL PURPOSES ONLY and is NOT medical advice.
 * Always consult your doctor or pharmacist before starting, stopping, or 
 * changing any medication.
 * 
 * Features:
 * - AI-powered interaction analysis
 * - Severity levels (major, moderate, minor)
 * - Detailed explanations
 * - Recommendations to consult healthcare providers
 */

import API from '@/utils/api';

// Important disclaimer text
export const DISCLAIMER = {
  short: 'For informational purposes only. Always consult your doctor or pharmacist.',
  full: 'This information is for educational purposes only and is not intended as medical advice. Drug interactions can vary based on individual factors. Always consult your doctor, pharmacist, or other qualified healthcare provider before starting, stopping, or changing any medication.',
  action: 'Please discuss with your doctor or pharmacist before taking these medications together.',
};

// Interaction severity levels
export const SEVERITY = {
  MAJOR: 'major',
  MODERATE: 'moderate',
  MINOR: 'minor',
  NONE: 'none',
};

// Severity colors for UI
export const SEVERITY_COLORS = {
  [SEVERITY.MAJOR]: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    border: 'border-red-200',
    icon: 'text-red-600',
  },
  [SEVERITY.MODERATE]: {
    bg: 'bg-amber-100',
    text: 'text-amber-800',
    border: 'border-amber-200',
    icon: 'text-amber-600',
  },
  [SEVERITY.MINOR]: {
    bg: 'bg-yellow-50',
    text: 'text-yellow-800',
    border: 'border-yellow-200',
    icon: 'text-yellow-600',
  },
  [SEVERITY.NONE]: {
    bg: 'bg-green-50',
    text: 'text-green-800',
    border: 'border-green-200',
    icon: 'text-green-600',
  },
};

// Severity labels
export const SEVERITY_LABELS = {
  [SEVERITY.MAJOR]: 'Potential Major Interaction',
  [SEVERITY.MODERATE]: 'Potential Moderate Interaction',
  [SEVERITY.MINOR]: 'Potential Minor Interaction',
  [SEVERITY.NONE]: 'No Known Interactions Found',
};

/**
 * Check for drug interactions between a new medication and existing medications
 * 
 * @param {string} newMedication - Name of the new medication being added
 * @param {Array} existingMedications - Array of existing medication objects
 * @returns {Object} - Interaction check results
 */
export const checkDrugInteractions = async (newMedication, existingMedications) => {
  if (!newMedication || !existingMedications?.length) {
    return {
      success: true,
      hasInteractions: false,
      interactions: [],
      summary: 'No existing medications to check against.',
      disclaimer: DISCLAIMER.short,
    };
  }
  
  try {
    const response = await API.post('/medical/check-interactions', {
      new_medication: newMedication,
      existing_medications: existingMedications.map(m => ({
        name: m.name,
        dosage: m.dosage,
        frequency: m.frequency,
      })),
    });
    
    return {
      success: true,
      ...response.data,
      disclaimer: DISCLAIMER.full,
    };
  } catch (error) {
    console.error('[DrugInteraction] Check failed:', error);
    return {
      success: false,
      error: error.response?.data?.detail || error.message || 'Failed to check interactions',
      hasInteractions: false,
      interactions: [],
      disclaimer: DISCLAIMER.short,
    };
  }
};

/**
 * Get the highest severity from a list of interactions
 */
export const getHighestSeverity = (interactions) => {
  if (!interactions?.length) return SEVERITY.NONE;
  
  const severityOrder = [SEVERITY.MAJOR, SEVERITY.MODERATE, SEVERITY.MINOR, SEVERITY.NONE];
  
  for (const severity of severityOrder) {
    if (interactions.some(i => i.severity === severity)) {
      return severity;
    }
  }
  
  return SEVERITY.NONE;
};

/**
 * Format interaction for display
 */
export const formatInteraction = (interaction) => {
  const colors = SEVERITY_COLORS[interaction.severity] || SEVERITY_COLORS[SEVERITY.MINOR];
  const label = SEVERITY_LABELS[interaction.severity] || 'Potential Interaction';
  
  return {
    ...interaction,
    colors,
    label,
  };
};

/**
 * Check if user should be warned (major or moderate interactions)
 */
export const shouldWarnUser = (interactions) => {
  if (!interactions?.length) return false;
  return interactions.some(i => 
    i.severity === SEVERITY.MAJOR || i.severity === SEVERITY.MODERATE
  );
};

/**
 * Get interaction summary text
 */
export const getInteractionSummary = (interactions) => {
  if (!interactions?.length) {
    return 'No known interactions found in our database.';
  }
  
  const majorCount = interactions.filter(i => i.severity === SEVERITY.MAJOR).length;
  const moderateCount = interactions.filter(i => i.severity === SEVERITY.MODERATE).length;
  const minorCount = interactions.filter(i => i.severity === SEVERITY.MINOR).length;
  
  const parts = [];
  if (majorCount > 0) parts.push(`${majorCount} potential major`);
  if (moderateCount > 0) parts.push(`${moderateCount} potential moderate`);
  if (minorCount > 0) parts.push(`${minorCount} potential minor`);
  
  return `Found ${parts.join(', ')} interaction${interactions.length > 1 ? 's' : ''}. ${DISCLAIMER.action}`;
};

export default {
  checkDrugInteractions,
  getHighestSeverity,
  formatInteraction,
  shouldWarnUser,
  getInteractionSummary,
  SEVERITY,
  SEVERITY_COLORS,
  SEVERITY_LABELS,
  DISCLAIMER,
};
